/**
 * The interface catalogue (search estate plan, decision 3): every word of the interface — navbar, footer, the
 * article pages' frame, the session, the player, the failure cards and lines — keyed, one file per language. Page
 * copy is not here: pages are written per language, never translated.
 *
 * `{name}` in a value is a slot `fill` replaces.
 */
import type {FailureCopyKey} from '@product/quies/components/Failures/failureCopy'

/** Keyed by `Intl.PluralRules` categories; `other` always present. */
export type PluralForms = {other: string} & Partial<Record<Intl.LDMLPluralRule, string>>

export type InterfaceStrings = {
    nav: {
        beginSession: string
        faq: string
        pricing: string
        legal: string
        signIn: string
        viewPricing: string
        signOut: string
        /** The account button's name: it shows an icon alone. */
        account: string
        menu: string
        closeMenu: string
        mainNav: string
        /** `{n}`: the sessions left, in the account trigger and on a plan. */
        sessions: PluralForms
    }
    footer: {
        selfHypnosisFor: string
        anxiety: string
        sleep: string
        stress: string
        confidence: string
        focus: string
        learn: string
        ericksonian: string
        aiHow: string
        vsMeditation: string
        methods: string
        languages: string
    }
    /** The frame of the article pages (condition, method, local), around their own copy. */
    page: {
        cta: string
        hint: string
        exploreFurther: string
        disclaimer: string
        transcript: string
        transcriptNote: string
        breadcrumb: string
    }
    pricing: {
        selectPlan: string
        /** The plan's button. */
        subscribe: string
        /** After the plan's price: `$14.99 a month`. */
        perMonth: string
        manageSubscription: string
        /** Said on the pricing page: replay is never charged. */
        replayFree: string
        /** The offer's one line, in the session: what a further session gives. */
        offerLead: string
    }
    session: {
        chooseLanguage: string
        changeLanguage: string
        changeVoice: string
        /** `{voice}`: the voice's name. */
        previewVoice: string
        stopPreview: string
        continue: string
        composing: string
        preparing: string
        pickingUp: string
        today: string
        yesterday: string
        loading: string
        /** `{count}`: the earlier turns not shown yet. */
        showEarlier: string
        tryDifferentApproach: string
        nothingAdded: string
        purchaseConfirmed: string
        /** The line under Begin on a guest's first visit: the rule, told before it applies. */
        firstSessionNote: string
    }
    /** The threshold: the sign-in a guest meets between the conversation and the session they listen to. */
    threshold: {
        title: string
        message: string
        google: string
        sendLink: string
        sending: string
        emailLabel: string
        or: string
        waitingTitle: string
        /** `{email}`: where the link went. */
        waitingBody: string
        sendAgain: string
        googleInstead: string
        /** The code field under the waiting view: its label, its button, its button while checking, and the two
         *  answers (the digits are not the code / there is no live code to check). */
        codeLabel: string
        codeSubmit: string
        codeSubmitting: string
        codeWrong: string
        codeDead: string
        errors: {
            invalidEmail: string
            rateLimited: string
            sessionError: string
            sendFailed: string
            sessionExpired: string
            generic: string
        }
    }
    player: {
        play: string
        pause: string
        playAgain: string
        seek: string
        eyesClosed: string
        openEyes: string
        paused: string
        unavailable: string
        /** A meditation without its own title, in the player and on the lock screen. */
        untitled: string
    }
    failure: Record<FailureCopyKey, string>
}

export function fill(template: string, slots: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in slots ? String(slots[name]) : whole))
}

export function plural(forms: PluralForms, lang: string, n: number): string {
    const category = new Intl.PluralRules(lang).select(n)
    return fill(forms[category] ?? forms.other, {n})
}
